import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toFrenchNumber } from "../../../components/formatted/FormattedText";
import ConfirmDeleteModal from "../../../ui/components/ConfirmDeleteModal";
import {
  addConfrontation,
  deleteConfrontation,
  getConfrontations,
  updateConfrontation,
} from "../../../services/container";

const toNumberOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim().replace(",", ".");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const getObjectIdValue = (value) => {
  if (!value) return null;
  const isValidObjectIdString = (s) => /^[a-fA-F0-9]{24}$/.test(String(s));

  if (typeof value === "string") {
    return isValidObjectIdString(value) ? value : null;
  }
  if (typeof value === "object") {
    if (value._id && isValidObjectIdString(value._id)) return String(value._id);
    if (value.id && isValidObjectIdString(value.id)) return String(value.id);
    if (value.value && isValidObjectIdString(value.value))
      return String(value.value);
    if (
      value.wasteCategoryValue &&
      isValidObjectIdString(value.wasteCategoryValue)
    ) {
      return String(value.wasteCategoryValue);
    }
  }
  return null;
};

const getWmae = (rows, manualKey, aiKey) => {
  const usable = rows.filter((row) => {
    const manualValue = row[manualKey];
    const aiValue = row[aiKey];
    return (
      manualValue != null && aiValue != null && manualValue >= 0 && aiValue >= 0
    );
  });
  const totalManual = usable.reduce((sum, row) => sum + row[manualKey], 0);
  if (totalManual <= 0) return null;
  const weightedError = usable.reduce((sum, row) => {
    return (
      sum +
      (row[manualKey] / totalManual) * Math.abs(row[aiKey] - row[manualKey])
    );
  }, 0);
  return weightedError;
};

const ConfrontationAnalysisSection = ({
  parentCategoriesList = [],
  totalWasteMass = 0,
  totalWeight = 0,
  hasFines = false,
  finesMassPercent = 0,
  period = null,
  clientParsed = null,
  zone = null,
}) => {
  const { t } = useTranslation();
  const [rowsState, setRowsState] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingConfrontationId, setExistingConfrontationId] = useState(null);
  const [initialManualByCategory, setInitialManualByCategory] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const characterizationDateRaw =
    period?.periodMaterialStopSubfilter ||
    period?.periodMaterialStop ||
    period?.start ||
    period?._time ||
    null;

  const characterizationDate = useMemo(
    () => (characterizationDateRaw ? new Date(characterizationDateRaw) : null),
    [characterizationDateRaw],
  );

  const safeTotalWeight = Number(totalWeight) > 0 ? Number(totalWeight) : null;
  const wasteCategoryId = useMemo(() => {
    return (
      getObjectIdValue(period?.wasteCategory) ||
      getObjectIdValue(period?.wasteCategoryName) ||
      getObjectIdValue(period?.waste_category) ||
      getObjectIdValue(period?.wasteCategoryId) ||
      getObjectIdValue(clientParsed?.wasteCategory) ||
      getObjectIdValue(clientParsed?.wasteCategoryName) ||
      getObjectIdValue(clientParsed?.wasteCategoryValue)
    );
  }, [period, clientParsed]);

  const wasteProducerClientId = useMemo(() => {
    return (
      getObjectIdValue(period?.mongoClientId) ||
      getObjectIdValue(period?.clientMongoId) ||
      getObjectIdValue(period?.client?._id) ||
      getObjectIdValue(clientParsed?.mongoClientId)
    );
  }, [period, clientParsed]);

  const iaRows = useMemo(() => {
    return (parentCategoriesList || []).map((parentCategory, index) => {
      const aiMassPercentBase =
        totalWasteMass > 0
          ? (Number(parentCategory?.massObjects || 0) / totalWasteMass) * 100
          : 0;
      const isOtherCategory = parentCategory?.parentCategory === "other";
      const aiMassPercent =
        isOtherCategory && hasFines
          ? aiMassPercentBase + Number(finesMassPercent || 0)
          : aiMassPercentBase;
      return {
        parentCategory: parentCategory?.parentCategory || `row-${index}`,
        sortOrder: index,
        aiMassPercent,
      };
    });
  }, [parentCategoriesList, totalWasteMass, hasFines, finesMassPercent]);

  useEffect(() => {
    const initialRows = iaRows.map((row) => ({
      ...row,
      manualMassPercent: "",
    }));
    setRowsState(initialRows);
    setInitialManualByCategory(
      initialRows.reduce((acc, row) => {
        acc[row.parentCategory] = row.manualMassPercent;
        return acc;
      }, {}),
    );
  }, [iaRows]);

  useEffect(() => {
    const loadConfrontation = async () => {
      if (!characterizationDate || !wasteCategoryId) return;
      const dayStart = new Date(characterizationDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayStop = new Date(characterizationDate);
      dayStop.setHours(23, 59, 59, 999);
      setIsLoading(true);
      try {
        const response = await getConfrontations({
          start: dayStart.toISOString(),
          stop: dayStop.toISOString(),
        });
        const list = response?.data?.confrontations || [];
        const matching = list.find((item) => {
          const itemWasteCategoryId = getObjectIdValue(item?.wasteCategory);
          if (!itemWasteCategoryId || itemWasteCategoryId !== wasteCategoryId) {
            return false;
          }
          if (wasteProducerClientId) {
            const itemClientId = getObjectIdValue(item?.wasteProducerClient);
            if (itemClientId && itemClientId !== wasteProducerClientId) {
              return false;
            }
          }
          if (!characterizationDateRaw || !item?.characterizationDate)
            return true;
          return (
            new Date(item.characterizationDate).getTime() ===
            new Date(characterizationDateRaw).getTime()
          );
        });

        if (!matching) {
          setExistingConfrontationId(null);
          return;
        }

        setExistingConfrontationId(matching._id);
        const rowsByCategory = (matching.parentCategoryRows || []).reduce(
          (acc, row) => {
            acc[row.parentCategory] = row;
            return acc;
          },
          {},
        );
        const nextRows = iaRows.map((row) => {
          const persisted = rowsByCategory[row.parentCategory];
          return {
            ...row,
            manualMassPercent:
              persisted?.manualMassPercent == null
                ? ""
                : String(persisted.manualMassPercent),
          };
        });
        setRowsState(nextRows);
        setInitialManualByCategory(
          nextRows.reduce((acc, row) => {
            acc[row.parentCategory] = row.manualMassPercent;
            return acc;
          }, {}),
        );
      } catch (error) {
        setErrorMessage(t("Failed to load confrontation data"));
      } finally {
        setIsLoading(false);
      }
    };
    loadConfrontation();
  }, [
    characterizationDate,
    characterizationDateRaw,
    wasteCategoryId,
    wasteProducerClientId,
    t,
    iaRows,
  ]);

  const computedRows = useMemo(() => {
    return rowsState.map((row) => {
      const manualMassPercent = toNumberOrNull(row.manualMassPercent);
      const aiMassPercent = Number(row.aiMassPercent || 0);

      return {
        ...row,
        aiMassPercent,
        manualMassPercent,
      };
    });
  }, [rowsState]);

  const hasAnyManualInput = useMemo(() => {
    // "Manquant" => input vide => toNumberOrNull("") === null
    // Donc on ne compte ici que les valeurs numériques (y compris 0).
    return computedRows.some((row) => row.manualMassPercent != null);
  }, [computedRows]);

  const toComparableManualValue = (value) => {
    const parsed = toNumberOrNull(value);
    if (parsed == null) return null;
    return Number(parsed.toFixed(6));
  };

  const hasManualChanges = useMemo(() => {
    return rowsState.some((row) => {
      const initialValue = initialManualByCategory[row.parentCategory] ?? "";
      return (
        toComparableManualValue(row.manualMassPercent) !==
        toComparableManualValue(initialValue)
      );
    });
  }, [rowsState, initialManualByCategory]);

  const wmaePercent = useMemo(
    () => getWmae(computedRows, "manualMassPercent", "aiMassPercent"),
    [computedRows],
  );

  const payloadRows = useMemo(() => {
    return computedRows
      .filter((row) => row.manualMassPercent != null)
      .map((row) => ({
        parentCategory: row.parentCategory,
        manualMassPercent: row.manualMassPercent,
        aiMassPercent: row.aiMassPercent ?? 0,
        error:
          row.manualMassPercent != null
            ? Math.abs((row.aiMassPercent ?? 0) - row.manualMassPercent)
            : undefined,
        sortOrder: row.sortOrder,
      }));
  }, [computedRows]);

  const sanitizeDecimalInput = (value) => {
    const raw = String(value ?? "");
    if (!raw) return "";
    let output = "";
    let hasSeparator = false;
    for (const char of raw) {
      const isDigit = char >= "0" && char <= "9";
      if (isDigit) {
        output += char;
        continue;
      }
      const isSeparator = char === "," || char === ".";
      if (isSeparator && !hasSeparator) {
        // Keep a single normalized decimal separator in UI state.
        output += ",";
        hasSeparator = true;
      }
    }
    return output;
  };

  const handleManualChange = (index, key, value) => {
    const sanitized = sanitizeDecimalInput(value);
    setErrorMessage("");
    setSuccessMessage("");
    setRowsState((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        return {
          ...row,
          [key]: sanitized,
        };
      }),
    );
  };

  const handleNumericPaste = (event, index, key) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text");
    const sanitized = sanitizeDecimalInput(pasted);
    handleManualChange(index, key, sanitized);
  };

  const buildPayload = ({ isSnapshot = false } = {}) => {
    if (!characterizationDateRaw) {
      throw new Error(t("Missing characterization date"));
    }
    if (!wasteCategoryId) {
      throw new Error(t("Missing waste category identifier"));
    }
    if (!hasAnyManualInput) {
      throw new Error(t("Please enter at least one manual value"));
    }
    return {
      characterizationDate: new Date(characterizationDateRaw).toISOString(),
      wasteCategory: wasteCategoryId,
      characterizationWeightKg: safeTotalWeight ?? 0,
      wasteProducerClient: wasteProducerClientId || undefined,
      wmae: wmaePercent ?? 0,
      isSnapshot,
      parentCategoryRows: payloadRows,
    };
  };

  const handleValidate = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setIsSaving(true);
    try {
      const payload = buildPayload({ isSnapshot: false });
      let response;
      if (existingConfrontationId) {
        response = await updateConfrontation(existingConfrontationId, payload);
      } else {
        response = await addConfrontation(payload);
      }
      const confrontationId = response?.data?.confrontation?._id;
      if (confrontationId) {
        setExistingConfrontationId(confrontationId);
      }
      setSuccessMessage(t("Confrontation saved successfully"));
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error ||
          error?.message ||
          t("Failed to save confrontation"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateSnapshot = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setIsSaving(true);
    try {
      const payload = buildPayload({ isSnapshot: true });
      await addConfrontation(payload);
      setSuccessMessage(t("Confrontation snapshot created"));
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error ||
          error?.message ||
          t("Failed to create confrontation snapshot"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const resetManualRowsToIa = () => {
    const nextRows = iaRows.map((row) => ({
      ...row,
      manualMassPercent: "",
    }));
    setRowsState(nextRows);
    setInitialManualByCategory(
      nextRows.reduce((acc, row) => {
        acc[row.parentCategory] = row.manualMassPercent;
        return acc;
      }, {}),
    );
    setExistingConfrontationId(null);
  };

  const handleDeleteConfrontation = async () => {
    if (!existingConfrontationId || isDeleting) return;
    setErrorMessage("");
    setSuccessMessage("");
    setIsDeleting(true);
    try {
      await deleteConfrontation(existingConfrontationId);
      resetManualRowsToIa();
      setShowDeleteModal(false);
      setSuccessMessage(t("confrontation.deletedSuccess"));
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error ||
          error?.message ||
          t("confrontation.deleteFailed"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const zoneName = zone?.addressName || zone?.name || "-";
  const clientName = clientParsed?.name || "-";
  const isActionDisabled =
    isSaving || isLoading || isDeleting || !wasteCategoryId;

  return (
    <div className="char-reports-section">
      <h2 className="char-reports-section-title">
        {t("Confrontation analysis")}
      </h2>
      <p className="char-text-14 char-text-secondary mb-4">
        {t(
          "Compare AI mass estimates with manual field measurements for the current characterization.",
        )}
      </p>

      <div className="char-reports-table-container mb-4 p-3 bg-gray-50 text-sm text-gray-700">
        <div>
          {t("Site")}: {zoneName}
        </div>
        <div>
          {t("Client")}: {clientName}
        </div>
      </div>

      <div className="char-reports-table-container overflow-auto bg-white">
        <div className="char-reports-table-header sticky top-0 z-10 bg-white">
          <div className="char-reports-table-header-label char-reports-table-header-col-1-3 min-w-0">
            <span className="whitespace-normal break-words block">
              {t("Material")}
            </span>
          </div>
          <div className="char-reports-table-header-col-2-3">
            <div className="grid grid-cols-3 w-full items-center gap-x-2">
              <div className="text-right char-text-14 min-w-0">
                {t("AI mass (%)")}
              </div>
              <div className="text-right char-text-14 min-w-0">
                {t("Manual mass (%)")}
              </div>
              <div className="text-right char-text-14 min-w-0">
                {t("Gap (%)")}
              </div>
            </div>
          </div>
        </div>

        <div className="pb-0">
          {computedRows.map((row, index) => {
            const gapPercent =
              row.manualMassPercent != null && row.aiMassPercent != null
                ? row.aiMassPercent - row.manualMassPercent
                : null;
            return (
              <div
                key={`${row.parentCategory}-${row.sortOrder}`}
                className="char-reports-table-row"
              >
                <div className="char-reports-table-row-cell char-reports-table-row-cell-1-3 min-w-0">
                  <div
                    className="char-text-14 whitespace-normal break-words"
                    title={t(row.parentCategory)}
                  >
                    {row.parentCategory === "other"
                      ? t("Autre + FINES")
                      : t(row.parentCategory)}
                  </div>
                </div>
                <div className="char-reports-table-row-cell char-reports-table-row-cell-2-3">
                  <div className="grid grid-cols-3 w-full items-center gap-x-2">
                    <div className="text-right char-text-14 min-w-0">
                      {toFrenchNumber(row.aiMassPercent, 2)}%
                    </div>
                    <div className="text-right char-text-14 min-w-0 flex justify-end">
                      <input
                        type="text"
                        autoComplete="off"
                        className="w-2/3 max-w-full text-right border-0 rounded px-1 py-0.5 bg-transparent focus:outline-none focus:ring-1 focus:ring-gray-300"
                        value={rowsState[index]?.manualMassPercent ?? ""}
                        onPaste={(event) =>
                          handleNumericPaste(event, index, "manualMassPercent")
                        }
                        onChange={(event) =>
                          handleManualChange(
                            index,
                            "manualMassPercent",
                            event.target.value,
                          )
                        }
                      />
                    </div>
                    <div className="text-right char-text-14 min-w-0">
                      {gapPercent == null
                        ? "-"
                        : `${toFrenchNumber(gapPercent, 2)}%`}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="char-reports-table-container mt-4 p-3">
        <div className="font-semibold mb-2">
          {t("Weighted absolute mass error")}
        </div>
        {wmaePercent != null && (
          <div className="text-sm">
            {t("WMAE (%)")}:{" "}
            <strong>
              {toFrenchNumber(wmaePercent, 2)}% -{" "}
              {t("Global weighted average mass error: {{value}}% of stream", {
                value: toFrenchNumber(wmaePercent, 2),
              })}
            </strong>
          </div>
        )}
        {wmaePercent == null && (
          <div className="text-sm text-gray-600">
            {t("No data to compute WMAE")}
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="mt-3 text-sm text-red-700">{errorMessage}</div>
      )}
      {successMessage && (
        <div className="mt-3 text-sm text-green-700">{successMessage}</div>
      )}
      {!wasteCategoryId && (
        <div className="mt-3 text-sm text-amber-700">
          {t(
            "Missing waste category identifier, confrontation cannot be saved.",
          )}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="char-button char-button-primary"
          disabled={isActionDisabled}
          onClick={handleCreateSnapshot}
        >
          {t("Save confrontation snapshot")}
        </button>
        {hasManualChanges && (
          <button
            type="button"
            className="char-button"
            disabled={isActionDisabled}
            onClick={handleValidate}
          >
            {isSaving ? t("Saving...") : t("Validate")}
          </button>
        )}
        {existingConfrontationId ? (
          <button
            type="button"
            className="char-button"
            disabled={isActionDisabled}
            onClick={() => setShowDeleteModal(true)}
          >
            {t("Delete confrontation")}
          </button>
        ) : null}
      </div>
      {showDeleteModal && existingConfrontationId ? (
        <ConfirmDeleteModal
          isOpen={showDeleteModal}
          title={t("Deletion")}
          message={t("confrontation.deleteConfirm")}
          confirmLabel={t("Delete")}
          cancelLabel={t("Cancel")}
          onConfirm={handleDeleteConfrontation}
          onCancel={() => setShowDeleteModal(false)}
        />
      ) : null}
    </div>
  );
};

export default ConfrontationAnalysisSection;
